import type { NavMeshWireSnapshot } from '../../match-init/nav-mesh-wire';

import type { TerraformingMapGenDebug } from './terraforming-map-gen-debug';

/**
 * Shared numeric stages for the plantable surface track (schema fields, bootstrap arrays, AGE state).
 *
 * Valid pairings with {@link ObstacleTileStage}:
 * - `None` + `None` — POI / inert tile (basecamp, seed stations, seed pads; no walking flip, no obstacle progression).
 * - `Open` / `Grass` / `Flower` + `None` — growable track (walk to plant, progress toward grass goal).
 * - `None` + `Sprout` / `Sapling` / `Tree` — obstruction track (match `obstacleTier` applies walking/burst rules).
 *
 * Any other combination is invalid.
 */
export enum TerrainSurfaceStage {
  None = 0,
  Open = 1,
  Grass = 2,
  Flower = 3
}

/**
 * Obstruction growth for per-tile schema fields and match-wide `obstacleTier`.
 *
 * Sprout is walkable. Sapling blocks walking. Tree blocks walking and seed effects.
 * See {@link TerrainSurfaceStage} for valid tile pairings. None is tile-only (POI).
 */
export enum ObstacleTileStage {
  None = 0,
  Sprout = 1,
  Sapling = 2,
  Tree = 3
}

/** Returns whether `surfaceStage` and `obstacleStage` form a legal tile (POI, growable, or obstacle). */
export function isValidTerrainTileStage(
  surfaceStage: TerrainSurfaceStage,
  obstacleStage: ObstacleTileStage
): boolean {
  if (obstacleStage !== ObstacleTileStage.None) {
    return surfaceStage === TerrainSurfaceStage.None;
  }

  return true;
}

export interface TerrainProgressState {
  totalGrowableTiles: number;
  grassTiles: number;
  flowerTiles: number;
  grassCoverageRatio: number;
  grassGoalProgressRatio: number;
  flowersOnGoalGrassRatio: number;
}

/** TF pathing mesh ids — one source of truth for AGE register keys and bootstrap Record keys. */
export const terraformingNavMeshIds = {
  open: 'open',
  blocked: 'blocked'
} as const;

export type TerraformingNavMeshId = (typeof terraformingNavMeshIds)[keyof typeof terraformingNavMeshIds];

export function isTerraformingNavMeshId(value: unknown): value is TerraformingNavMeshId {
  return value === terraformingNavMeshIds.open || value === terraformingNavMeshIds.blocked;
}

/** One TF pathing mesh on the wire (boundary + holes + triangulation). */
export type TerraformingNavMeshSnapshot = NavMeshWireSnapshot;

/**
 * TF pathing meshes keyed by {@link TerraformingNavMeshId}.
 * Clients register every entry on one runtime and activate open until gameplay or debug switches.
 */
export type TerraformingPathingNavMeshes = Record<TerraformingNavMeshId, TerraformingNavMeshSnapshot>;

export interface TerraformingTerrainSnapshot {
  mapSeed: string;
  widthTiles: number;
  heightTiles: number;
  revision: number;
  stagesByTileIndex: number[];
  growableTileIndices: number[];
  progressTileIndices: number[];
  obstructionTileIndices: number[];
  /** One-tile outer ring (POI on wire; not walkable / not in progress). */
  borderTileIndices: number[];
  /** Full basecamp footprint including seed-station tiles (POI on wire). */
  basecampTileIndices: number[];
  seedBurstPadTileIndices: number[];
  seedStationTileIndices: number[];
  /** Config playable size (WYSIWYG with procedural map config). */
  playableWidthTiles: number;
  playableHeightTiles: number;
  /** Presentation ring outside playable (from procedural `presentation.borderOutsetTiles`, default 1). */
  borderOutsetTiles: number;
}

export interface SeedBurstPadState {
  id: string;
  /** Reference tile for the pad footprint; not necessarily geometric center (e.g. 4×4 pads anchor on one corner tile). */
  anchorTileIndex: number;
  footprintTileIndices: number[];
  radiusPx: number;
  chargeRatio: number;
  active: boolean;
  playerCount: number;
  firedCount: number;
}

export interface GuideTargetState {
  playerId: string;
  targetKind: number;
  tileIndex: number;
  revision: number;
}

/** Full authoritative preload payload for TF clients and late join. */
export interface TerraformingClientBootstrap {
  terrain: TerraformingTerrainSnapshot;
  obstacleTier: ObstacleTileStage;
  progress: TerrainProgressState;
  seedBurstPads: SeedBurstPadState[];
  guideTargets: GuideTargetState[];
  /** Open + Blocked pathing for remote prediction and overlays (same ids as AGE runtime). */
  pathingNavMeshes: TerraformingPathingNavMeshes;
  /** Which of {@link pathingNavMeshes} is live for pathing. */
  activeNavMesh: TerraformingNavMeshId;
  /**
   * Layout-generation audit for the TF match debug panel.
   * Present only when the engine ran with `debugMode` (see {@link TerraformingMapGenDebug}).
   */
  mapGenDebug?: TerraformingMapGenDebug;
}
